import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import { API_CONFIG } from '@/lib/config/api.config'
import type {
  DashboardStats,
  ExecutionSummaryResponse,
  TestCaseHistoryResponse,
  ExecutionTrendResponse,
  DashboardMetrics,
  MyMetrics,
  QualityTrendResponse,
  ExecutiveReport,
  ManagementReport,
  ProjectHealthResponse,
  CoverageReport,
} from '@/types/report.types'

// ============================================================================
// Query Keys
// ============================================================================

export const reportKeys = {
  all: ['reports'] as const,
  dashboard: (projectId?: number) => [...reportKeys.all, 'dashboard', projectId] as const,
  executionSummary: (params?: Record<string, unknown>) =>
    [...reportKeys.all, 'execution-summary', params] as const,
  testplanReport: (testplanId: number) => [...reportKeys.all, 'testplan', testplanId] as const,
  testcaseHistory: (testcaseId: number) => [...reportKeys.all, 'testcase-history', testcaseId] as const,
  executionTrends: (params?: Record<string, unknown>) =>
    [...reportKeys.all, 'trends', params] as const,
  dashboardMetrics: (projectId?: number) =>
    [...reportKeys.all, 'dashboard-metrics', projectId] as const,
  myMetrics: () => [...reportKeys.all, 'my-metrics'] as const,
  myExecutionTrend: (days?: number) =>
    [...reportKeys.all, 'my-execution-trend', days] as const,
  qualityTrend: (params?: Record<string, unknown>) =>
    [...reportKeys.all, 'quality-trend', params] as const,
  executive: (params?: Record<string, unknown>) =>
    [...reportKeys.all, 'executive', params] as const,
  management: (params?: Record<string, unknown>) =>
    [...reportKeys.all, 'management', params] as const,
  projectHealth: (projectId?: number) =>
    [...reportKeys.all, 'project-health', projectId] as const,
  coverage: (projectId?: number) =>
    [...reportKeys.all, 'coverage', projectId] as const,
}

// ============================================================================
// Dashboard & Summary Reports
// ============================================================================

export const useDashboard = (projectId?: number) => {
  return useQuery({
    queryKey: reportKeys.dashboard(projectId),
    queryFn: async () => {
      const params = new URLSearchParams()
      if (projectId) params.append('project_id', projectId.toString())

      const { data } = await apiClient.get<DashboardStats>(
        `${API_CONFIG.ENDPOINTS.REPORTS.DASHBOARD}?${params.toString()}`
      )
      return data
    },
  })
}

export const useExecutionSummary = (params?: {
  project_id?: number
  testplan_id?: number
  start_date?: string
  end_date?: string
}) => {
  return useQuery({
    queryKey: reportKeys.executionSummary(params),
    queryFn: async () => {
      const queryParams = new URLSearchParams()
      if (params?.project_id)
        queryParams.append('project_id', params.project_id.toString())
      if (params?.testplan_id)
        queryParams.append('testplan_id', params.testplan_id.toString())
      if (params?.start_date)
        queryParams.append('start_date', params.start_date)
      if (params?.end_date)
        queryParams.append('end_date', params.end_date)

      const { data } = await apiClient.get<ExecutionSummaryResponse>(
        `${API_CONFIG.ENDPOINTS.REPORTS.EXECUTION_SUMMARY}?${queryParams.toString()}`
      )
      return data
    },
  })
}

export const useTestplanReport = (testplanId: number) => {
  return useQuery({
    queryKey: reportKeys.testplanReport(testplanId),
    queryFn: async () => {
      const { data } = await apiClient.get(
        API_CONFIG.ENDPOINTS.REPORTS.TESTPLAN_REPORT(testplanId.toString())
      )
      return data
    },
    enabled: !!testplanId,
  })
}

export const useTestCaseHistory = (testcaseId: number, limit?: number) => {
  return useQuery({
    queryKey: reportKeys.testcaseHistory(testcaseId),
    queryFn: async () => {
      const params = new URLSearchParams()
      if (limit) params.append('limit', limit.toString())

      const { data } = await apiClient.get<TestCaseHistoryResponse>(
        `${API_CONFIG.ENDPOINTS.REPORTS.TESTCASE_HISTORY(testcaseId.toString())}?${params.toString()}`
      )
      return data.history ?? []
    },
    enabled: !!testcaseId,
  })
}

// ============================================================================
// Trends & Analytics
// ============================================================================

export const useExecutionTrends = (params?: {
  project_id?: number
  days?: number
  start_date?: string
  end_date?: string
}) => {
  return useQuery({
    queryKey: reportKeys.executionTrends(params),
    queryFn: async () => {
      const queryParams = new URLSearchParams()
      if (params?.project_id)
        queryParams.append('project_id', params.project_id.toString())
      if (params?.days)
        queryParams.append('days', params.days.toString())
      if (params?.start_date)
        queryParams.append('start_date', params.start_date)
      if (params?.end_date)
        queryParams.append('end_date', params.end_date)

      const { data } = await apiClient.get<ExecutionTrendResponse>(
        `${API_CONFIG.ENDPOINTS.REPORTS.TRENDS_EXECUTION}?${queryParams.toString()}`
      )
      return data.trend ?? []
    },
  })
}

export const useQualityTrend = (params?: {
  project_id?: number
  days?: number
  start_date?: string
  end_date?: string
}) => {
  return useQuery({
    queryKey: reportKeys.qualityTrend(params),
    queryFn: async () => {
      const queryParams = new URLSearchParams()
      if (params?.project_id)
        queryParams.append('project_id', params.project_id.toString())
      if (params?.days)
        queryParams.append('days', params.days.toString())
      if (params?.start_date)
        queryParams.append('start_date', params.start_date)
      if (params?.end_date)
        queryParams.append('end_date', params.end_date)

      const { data } = await apiClient.get<QualityTrendResponse>(
        `${API_CONFIG.ENDPOINTS.REPORTS.QUALITY_TREND}?${queryParams.toString()}`
      )
      return data.trend ?? []
    },
  })
}

// ============================================================================
// Dashboard Metrics
// ============================================================================

export const useDashboardMetrics = (projectId?: number) => {
  return useQuery({
    queryKey: reportKeys.dashboardMetrics(projectId),
    queryFn: async () => {
      const params = new URLSearchParams()
      if (projectId) params.append('project_id', projectId.toString())

      const { data } = await apiClient.get<DashboardMetrics>(
        `${API_CONFIG.ENDPOINTS.REPORTS.DASHBOARD_METRICS}?${params.toString()}`
      )
      return data
    },
  })
}

export const useMyMetrics = () => {
  return useQuery({
    queryKey: reportKeys.myMetrics(),
    queryFn: async () => {
      const { data } = await apiClient.get<MyMetrics>(
        API_CONFIG.ENDPOINTS.REPORTS.MY_METRICS
      )
      return data
    },
  })
}

export const useMyExecutionTrend = (days: number = 30) => {
  return useQuery({
    queryKey: reportKeys.myExecutionTrend(days),
    queryFn: async () => {
      const params = new URLSearchParams()
      params.append('days', days.toString())

      const { data } = await apiClient.get<ExecutionTrendResponse>(
        `${API_CONFIG.ENDPOINTS.REPORTS.MY_EXECUTION_TREND}?${params.toString()}`
      )
      return data.trend ?? []
    },
  })
}

// ============================================================================
// Executive & Management Reports
// ============================================================================

export const useExecutiveReport = (params?: {
  start_date?: string
  end_date?: string
  project_id?: number
}) => {
  return useQuery({
    queryKey: reportKeys.executive(params),
    queryFn: async () => {
      const queryParams = new URLSearchParams()
      if (params?.start_date)
        queryParams.append('start_date', params.start_date)
      if (params?.end_date)
        queryParams.append('end_date', params.end_date)
      if (params?.project_id)
        queryParams.append('project_id', params.project_id.toString())

      const { data } = await apiClient.get<ExecutiveReport>(
        `${API_CONFIG.ENDPOINTS.REPORTS.EXECUTIVE}?${queryParams.toString()}`
      )
      return data
    },
  })
}

export const useManagementReport = (params?: {
  start_date?: string
  end_date?: string
  project_id?: number
}) => {
  return useQuery({
    queryKey: reportKeys.management(params),
    queryFn: async () => {
      const queryParams = new URLSearchParams()
      if (params?.start_date)
        queryParams.append('start_date', params.start_date)
      if (params?.end_date)
        queryParams.append('end_date', params.end_date)
      if (params?.project_id)
        queryParams.append('project_id', params.project_id.toString())

      const { data } = await apiClient.get<ManagementReport>(
        `${API_CONFIG.ENDPOINTS.REPORTS.MANAGEMENT}?${queryParams.toString()}`
      )
      return data
    },
  })
}

// ============================================================================
// Project Health & Coverage
// ============================================================================

export const useProjectHealth = (projectId?: number) => {
  return useQuery({
    queryKey: reportKeys.projectHealth(projectId),
    queryFn: async () => {
      const params = new URLSearchParams()
      if (projectId) params.append('project_id', projectId.toString())

      const { data } = await apiClient.get<ProjectHealthResponse>(
        `${API_CONFIG.ENDPOINTS.REPORTS.PROJECT_HEALTH}?${params.toString()}`
      )
      return data
    },
  })
}

export const useCoverageReport = (projectId?: number) => {
  return useQuery({
    queryKey: reportKeys.coverage(projectId),
    queryFn: async () => {
      const params = new URLSearchParams()
      if (projectId) params.append('project_id', projectId.toString())

      const { data } = await apiClient.get<CoverageReport>(
        `${API_CONFIG.ENDPOINTS.REPORTS.COVERAGE}?${params.toString()}`
      )
      return data
    },
  })
}
